/**
 * POM-5945 — reading the facility-transfer response envelope.
 *
 * The mutation used to return a bare `[Boolean!]!`. Both call sites checked it with
 * `if (isTransferred)`, and a non-empty array is always truthy — so a fully blocked transfer
 * rendered a success toast. The envelope replaces that check with explicit counts, and carries a
 * per-order reason the operator can act on.
 *
 * Shape mirrors the compounding send flow (SendToCentralFillResults), which solves the same problem.
 */
import { compact, uniq } from 'lodash';

/**
 * Operator-facing wording for each backend reason. Unknown values fall through to the raw code
 * rather than disappearing, so a reason added server-side is still visible before the portal ships
 * a label for it.
 */
export const TRANSFER_REASON_LABELS = {
  SHIPPING_STATE_UNKNOWN: 'Shipping state unknown',
  STATE_NOT_LICENSED: 'Facility not licensed for state',
};

export const labelForTransferReason = (reason) => TRANSFER_REASON_LABELS[reason] || reason;

/** Distinct reason labels across the blocked orders, in the order they were reported. */
export const formatTransferReasons = (outcomes = []) =>
  uniq(compact(outcomes.map(({ reason }) => reason)).map(labelForTransferReason)).join(', ');

export const TRANSFER_STATUS = {
  /** The request itself failed; handleError has already shown the error dialog. */
  ERROR: 'ERROR',
  ALL: 'ALL',
  PARTIAL: 'PARTIAL',
  NONE: 'NONE',
};

/**
 * Reduce the envelope to what the two call sites actually branch on.
 *
 * `undefined` means the thunk swallowed a rejection via handleError, which is NOT the same as
 * "nothing transferred": the error dialog is already up, and the caller must not treat it as a
 * completed request (no snackbar, no selection clear).
 *
 * Counts come from `totalTransferred` / `totalRequested`, never from the positional `results` array:
 * on the lineItemIds path `results` is indexed by DISTINCT ORDER, so three selected ids can come
 * back as two entries and position carries no meaning. The backend derives both counts from
 * `outcomes`, so they cannot disagree with the reasons reported here.
 */
export const summarizeTransferOutcomes = (response) => {
  if (!response) {
    return {
      status: TRANSFER_STATUS.ERROR,
      totalRequested: 0,
      totalTransferred: 0,
      blockedOutcomes: [],
      reasonSummary: '',
    };
  }

  const outcomes = response.outcomes || [];
  const totalRequested = response.totalRequested ?? outcomes.length;
  const totalTransferred = response.totalTransferred ?? 0;
  const blockedOutcomes = outcomes.filter(({ isTransferred }) => !isTransferred);

  const statusFor = () => {
    if (totalTransferred === 0) return TRANSFER_STATUS.NONE;
    if (totalTransferred < totalRequested) return TRANSFER_STATUS.PARTIAL;

    return TRANSFER_STATUS.ALL;
  };

  return {
    status: statusFor(),
    totalRequested,
    totalTransferred,
    blockedOutcomes,
    reasonSummary: formatTransferReasons(blockedOutcomes),
  };
};

/** `{n} order` / `{n} orders` — matches the compounding send dialog's phrasing. */
export const pluralizeOrders = (count) => `${count} order${count === 1 ? '' : 's'}`;

/**
 * Rows for the transfer results table, built ONLY from `outcomes`.
 *
 * Deliberately order-level and keyed by `orderId`: the transfer verdict is per ORDER, and
 * `outcomes[].lineItemIds` is EMPTY whenever the caller sent `orderIds` instead of line items, so it
 * cannot be relied on to attribute rows. Everything the table shows is already in the outcome.
 *
 * Not-transferred rows lead, since they are the ones the operator has to act on. Relative order
 * within each group is the backend's, which follows the caller's requested order.
 */
export const buildTransferResultRows = (outcomes) => {
  const rows = (outcomes || []).map(({ orderId, isTransferred, reason, resultingOrderId }) => ({
    orderId,
    isTransferred: !!isTransferred,
    reason: reason ?? null,
    resultingOrderId: resultingOrderId ?? null,
  }));

  return [
    ...rows.filter(({ isTransferred }) => !isTransferred),
    ...rows.filter(({ isTransferred }) => isTransferred),
  ];
};

/**
 * Whether the outcome needs the results table rather than a snackbar.
 *
 * PARTIAL always does — a toast cannot say WHICH orders failed, and by definition partial means at
 * least two orders were requested. A fully blocked batch does too, but only when more than one order
 * was requested: for a single order the existing error snackbar already names the one reason.
 *
 * ERROR never does. `summarizeTransferOutcomes` returns no outcomes for a swallowed rejection, so
 * showing the table would render an empty grid over a request that actually failed.
 */
export const shouldShowTransferResultsDialog = ({ status, totalRequested } = {}) => {
  if (status === TRANSFER_STATUS.PARTIAL) return true;
  if (status === TRANSFER_STATUS.NONE) return totalRequested > 1;

  return false;
};
